import { createSelector } from 'reselect'
import moment from 'moment'
import {
  getAllXcoverInsuranceItems,
  getInsuranceUpgradeSourceItem,
} from '../view/insurance'
import { getInsuranceDatesFromOrder } from 'lib/order/orderInsuranceUtils'
import getInsuranceDestinationCountriesFromCart, { getPassportCardDestinationCountriesFromCart } from '../view/getInsuranceDestinationCountriesFromCart'
import getInsuranceDestinationCountriesFromExistingOrder from '../view/getInsuranceDestinationCountriesFromExistingOrder'
import getPassportCardDestinationCountriesFromExistingOrder from '../view/getPassportCardDestinationCountriesFromExistingOrder'
import getInsuranceDatesFromCart from './getInsuranceDatesFromCart'
import { isSameDay } from 'lib/datetime/dateUtils'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'

export const getInsuranceDatesFromExistingOrder = createSelector(
  (state: App.State) => state.checkout.cart.existingOrder,
  (order) => {
    if (order) {
      return getInsuranceDatesFromOrder(order)
    }
  },
)

export const isPastHotelInsuranceCoverPeriod = createSelector(
  (state: App.State) => getInsuranceDatesFromCart(state),
  (state: App.State) => getInsuranceUpgradeSourceItem(state),
  (state: App.State) => state.checkout.cart.mode,
  (newDatesFromCart, insuranceUpgradeSourceItem, cartMode): boolean => {
    if (
      !insuranceUpgradeSourceItem ||
      !newDatesFromCart?.endDate ||
      !['change-dates', 'change-package'].includes(cartMode ?? '')
    ) {
      return false
    }
    return moment(insuranceUpgradeSourceItem?.createdAt).add(1, 'year').isBefore(newDatesFromCart?.endDate)
  },
)

interface InsuranceDates {
  startDate?: string
  endDate?: string
}
const EmptyDates: InsuranceDates = { startDate: undefined, endDate: undefined }

/**
 * Gets insurance dates from cart items only, without fallback to existing order dates.
 *
 * Use this selector for post-purchase flows (e.g., flight date change) where the cart
 * contains NEW dates that should override the existing order dates.
 *
 * @see getCheckoutInsuranceDates - Use that selector for normal checkout flows where
 * existing order dates are a valid fallback when cart dates are unavailable.
 */
export const getInsuranceDatesFromCartOnly = createSelector(
  (state: App.State) => getInsuranceDatesFromCart(state),
  (state: App.State) => getAllXcoverInsuranceItems(state)[0],
  (cartDates, item): InsuranceDates => {
    if (item?.startDate && item?.endDate) {
      // user has customised dates, return them
      return {
        startDate: item.startDate,
        endDate: item.endDate,
      }
    }

    /**
     * In one-way standalone flights, start date and end date are the same
     * We need to add 1 day to the end date, otherwise the insurance quote payload isn't valid
     */
    if (cartDates && isSameDay(new Date(cartDates.startDate), new Date(cartDates.endDate))) {
      return {
        startDate: cartDates.startDate,
        endDate: moment(cartDates.endDate).add(1, 'day').format(ISO_DATE_FORMAT),
      }
    }

    return cartDates ?? EmptyDates
  },
)

/**
 * Gets insurance dates with fallback logic: cart dates → existing order dates.
 *
 * Use this selector for normal checkout flows where the cart may not have dates yet,
 * and existing order dates are a valid fallback (e.g., adding insurance to an existing booking).
 *
 * @see getInsuranceDatesFromCartOnly - Use that selector for post-purchase flows where
 * cart dates should always take precedence (e.g., flight date change).
 */
export const getCheckoutInsuranceDates = createSelector(
  (state: App.State) => getInsuranceDatesFromCart(state),
  (state: App.State) => getInsuranceDatesFromExistingOrder(state),
  (state: App.State) => getAllXcoverInsuranceItems(state)[0],
  (cartDates, existingOrderDates, item): InsuranceDates => {
    if (item?.startDate && item?.endDate) {
      // user has customised dates, return them
      return {
        startDate: item.startDate,
        endDate: item.endDate,
      }
    }

    /**
     * In one-way standalone flights, start date and end date are the same
     * We need to add 1 day to the end date, otherwise the insurance quote payload isn't valid
     */
    if (cartDates && isSameDay(new Date(cartDates.startDate), new Date(cartDates.endDate))) {
      return {
        startDate: cartDates.startDate,
        endDate: moment(cartDates.endDate).add(1, 'day').format(ISO_DATE_FORMAT),
      }
    }
    if (existingOrderDates && isSameDay(new Date(existingOrderDates.startDate), new Date(existingOrderDates.endDate))) {
      return {
        startDate: existingOrderDates.startDate,
        endDate: moment(existingOrderDates.endDate).add(1, 'day').format(ISO_DATE_FORMAT),
      }
    }

    return existingOrderDates ?? cartDates ?? EmptyDates
  },
)

function resolveCheckoutCountries(
  cartCountries: Array<string>,
  orderCountries: Array<string>,
  item: { countries?: Array<string> } | undefined,
): Array<string> {
  if (item?.countries?.length) {
    return item.countries
  } else if (orderCountries.length) {
    return orderCountries
  } else if (cartCountries.length) {
    return cartCountries
  }
  return []
}

export const getCheckoutInsuranceCountries = createSelector(
  (state: App.State) => getInsuranceDestinationCountriesFromCart(state),
  (state: App.State) => getInsuranceDestinationCountriesFromExistingOrder(state),
  (state: App.State) => getAllXcoverInsuranceItems(state)[0],
  resolveCheckoutCountries,
)

/**
 * PassportCard variant: the cart side reads a tour's itinerary even when the departure
 * is not guaranteed, and the existing-order side derives countries from the items a
 * PassportCard policy covers, so cancelled history never describes the trip. Resolution
 * order matches getCheckoutInsuranceCountries: an explicit selection on the insurance
 * item wins, then an existing order, then the cart.
 */
export const getPassportCardCheckoutCountries = createSelector(
  (state: App.State) => getPassportCardDestinationCountriesFromCart(state),
  (state: App.State) => getPassportCardDestinationCountriesFromExistingOrder(state),
  (state: App.State) => getAllXcoverInsuranceItems(state)[0],
  resolveCheckoutCountries,
)
